import React, { useEffect, useRef } from "react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { EmbeddedMod } from "@src/store/slices/experienceBuilder/types";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useDispatch } from "@src/store";
import { ExperienceBuilderAction as Action } from "@src/store/slices/experienceBuilder";

import { DeleteButton } from "@src/features/experienceBuilder/modifications/common/DeleteButton";
import { useIsModHidden } from "@src/features/experienceBuilder/modifications/common/useIsModHidden";
import { VisibilityButton } from "@src/features/experienceBuilder/modifications/common/VisibilityButton";

interface ModificationListItemProps {
  mod: EmbeddedMod;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

const DELETE_BUTTON_CLASS = "ig-mod-delete";

export const ModificationListItem = ({
  mod,
  icon,
  title,
  subtitle,
}: ModificationListItemProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isModHidden = useIsModHidden(mod.id);
  // "Dwell" = hover that lasted 1+ second, used to trigger focus (e.g. scroll-to-element in iframe)
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => clearTimeout(dwellTimerRef.current);
  }, []);

  const onClick = () => {
    void dispatch(Action.editMod(mod));
  };
  const onMouseEnter = () => {
    dispatch(Action.hoverMod(mod.id));
    dwellTimerRef.current = setTimeout(() => {
      dispatch(Action.dwellMod(mod.id));
    }, 1000);
  };
  const onMouseLeave = () => {
    clearTimeout(dwellTimerRef.current);
    dispatch(Action.blurMod());
  };

  return (
    <Stack
      direction="row"
      spacing={4}
      alignItems="center"
      // No handler at all while hidden, rather than a guard inside one: a
      // hidden mod's result is not on screen, so opening its draft would put
      // the merchant in an editor they cannot see the effect of. Highlight and
      // focus stay wired — they point at the unmodified element.
      onClick={isModHidden ? undefined : onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        border: `1px solid ${isModHidden ? theme.igBorders.core.disabled : theme.igBorders.core.default}`,
        borderRadius: "6px",
        padding: 3,
        cursor: isModHidden ? "default" : "pointer",
        ...(isModHidden
          ? {}
          : {
              "&:hover": {
                backgroundColor: theme.igBackgroundColors.baseLightHigh,
              },
            }),

        [`& .${DELETE_BUTTON_CLASS}`]: {
          opacity: 0,
          pointerEvents: "none",
          transition: "opacity 120ms ease-in-out",
        },
        [`&:hover .${DELETE_BUTTON_CLASS}, &:focus-within .${DELETE_BUTTON_CLASS}`]:
          {
            opacity: 1,
            pointerEvents: "auto",
          },
        // Where hover does not exist there is nothing to reveal on, and a bin
        // that never appears is a bin that cannot be reached.
        "@media (hover: none)": {
          [`& .${DELETE_BUTTON_CLASS}`]: {
            opacity: 1,
            pointerEvents: "auto",
          },
        },
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          gap: 1,
          opacity: isModHidden ? 0.5 : 1,
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center" minWidth={0}>
          {icon}
          <IgTypography variant="body1" fontWeight={600} noWrap>
            {title}
          </IgTypography>
        </Stack>
        <IgTypography variant="caption" color="text.secondary" noWrap>
          {subtitle}
        </IgTypography>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        {!isModHidden && (
          <DeleteButton className={DELETE_BUTTON_CLASS} mod={mod} />
        )}
        <VisibilityButton mod={mod} />
      </Stack>
    </Stack>
  );
};
